import { Injectable } from '@angular/core';
import { OrderItem } from '../models/order-item.model';
import { Order } from '../models/order.model';
import { Product } from '../models/product.model';
import { TranslateService } from './translate.service';
import { SKU } from '../models/sku.model';
import { flatternArray, isEmptyArray } from '../shared/utils';
import { OrderItemSku } from '../models/order-item-sku.model';
import { EventNames } from '../enums/ga-event-names';
import { OrderTypes } from '../enums/order-types';

enum OrderTypesLabel {
  BuyingWindow = 'buying window',
  OnDemand = 'on demand',
  AutoShip = 'custom-request',
}

export enum GAPaymentType {
  Sap = 'SAP',
  CreditCard = 'Credit Card',
  Term = 'Terms',
}
const ORDER_TYPES = [
  { type: OrderTypes.PREORDER_TYPE, label: OrderTypesLabel.BuyingWindow },
  { type: OrderTypes.ECOMMERCE_TYPE, label: OrderTypesLabel.OnDemand },
  { type: OrderTypes.CUSTOM_TYPE, label: OrderTypesLabel.AutoShip },
];

interface Item {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category: string;
  item_variant?: string;
  item_list_name?: string;
  price: number;
  quantity?: number;
  coupon?: string; // promo_code
}

@Injectable()
export class GoogleAnalytics {
  private isUserIdSubmitted = false;
  private gtmId: string;

  constructor(private translateService: TranslateService) {}


  public initService() {
    if (this.gtmId) {
      return;
    }

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // needs to find GTM ID from window object
    // from object  window.google_tag_manager   find key that starts with 'GTM-'
    this.gtmId = Object.keys(window['google_tag_manager'] || {}).find((key) =>
      key.startsWith('GTM-')
    );
  }


  private reinitGTMWithUserId(userId: string): void {
    this.initService();

    if (!this.gtmId || !userId) {
      return;
    }

    this.sendGoogleAnalytics([
      'config',
      this.gtmId,
      {
        user_id: userId,
      },
    ]);
  }


  public submitUserId(userId: string): void {
    if (this.isUserIdSubmitted || !userId) {
      return;
    }

    this.reinitGTMWithUserId(userId);

    this.sendGoogleAnalytics({
      event: EventNames.Login,
      user_id: userId.toString(),
    });

    this.isUserIdSubmitted = true;
  }

  public submitLogout(userId: string) {
    if (!userId) {
      return;
    }
    this.sendGoogleAnalytics({
      event: EventNames.Logout,
      user_id: userId,
    })
  }

  // add_payment_info (paymentType)
  addPaymentInfo(order: Order, value: GAPaymentType): void {
    this.sendGoogleAnalytics({
      event: EventNames.AddPaymentInfo,
      ecommerce: {
        currency: 'USD',
        value: order.totalCost,
        payment_type: value,
        ...(order.promoCodeExists && { coupon: order.promo_code.code }),
        items: order.items?.length
          ? flatternArray(
              order.items.map((orderItem) =>
                this.mapOrderItemToProductData(orderItem)
              )
            )
          : [],
      },
    });
  }

  // add_shipping_info ()
  addShippingInfo(): void {
    // this.sendGoogleAnalytics({
    //   event: EventNames.AddShippingInfo,
    //   ecommerce: {},
    // });
  }

  // add_to_cart (value, currency, items)
  addToShoppingCart(
    order: Order,
    orderItem: OrderItem,
    quantity = 0,
    sku: OrderItemSku = null
  ): void {
    if (order && orderItem) {
      this.sendGoogleAnalytics({
        event: EventNames.AddToCart,
        ecommerce: {
          currency: 'USD',
          value: order.totalCost,
          items: this.mapOrderItemToProductData(
            // order,
            orderItem,
            quantity,
            sku
          ),
        },
      });
    }
  }

  // add_to_cart (value, currency, items)
  updateShoppingCart(order: Order, orderItem: OrderItem): void {
    if (order && orderItem) {
      if (orderItem.hasVariations) {
        orderItem.skus.forEach((orderSku) => {
          const quantityDiff = orderSku.quantity - orderSku.init_quantity;
          if (quantityDiff < 0) {
            this.removeFromShoppingCart(
              order,
              orderItem,
              quantityDiff * -1,
              orderSku
            );
          }
          if (quantityDiff > 0) {
            this.addToShoppingCart(order, orderItem, quantityDiff, orderSku);
          }
        });
      } else {
        const quantityDiff = orderItem.quantity - orderItem.init_quantity;
        if (quantityDiff < 0) {
          this.removeFromShoppingCart(order, orderItem, quantityDiff * -1);
        }
        if (quantityDiff > 0) {
          this.addToShoppingCart(order, orderItem, quantityDiff);
        }
      }
    }
  }

  // remove_from_cart	(value, currency, items)
  removeFromShoppingCart(
    order: Order,
    orderItem: OrderItem,
    quantity = 0,
    sku: OrderItemSku = null
  ): void {
    if (order && orderItem) {
      this.sendGoogleAnalytics({
        event: EventNames.RemoveFromCart,
        ecommerce: {
          currency: 'USD',
          value: order.totalCost,
          items: this.mapOrderItemToProductData(
            // order,
            orderItem,
            quantity,
            sku
          ),
        },
      });
    }
  }

  // search	search_term (text, orderType)
  searchTerm(term: string, order: Order): void {
    if (order) {
      this.sendGoogleAnalytics({
        event: EventNames.Search,
        search_term: term,
        order_type: ORDER_TYPES.find((i) => i.type === order.type).label,
      });
    }
  }

  // header_search_no_results search_term (text, orderType, page) - header search box, zero results only
  headerSearchNoResults(term: string, orderType: string, page: string): void {
    this.sendGoogleAnalytics({
      event: 'header_search_no_results',
      search_term: term,
      order_type:
        orderType === OrderTypes.PREORDER_TYPE
          ? OrderTypesLabel.BuyingWindow
          : OrderTypesLabel.OnDemand,
      page: page,
    });
  }

  // add/remove favourite add_to_wishlist (orderType, product)
  addToWishlist(product: Product, isOnDemand: boolean): void {
    if (product) {
      this.sendGoogleAnalytics({
        event: EventNames.AddToWishlist,
        ecommerce: {
          currency: 'USD',
          value: product.getPrice(0),
          wishlist_name: isOnDemand
            ? OrderTypesLabel.OnDemand
            : OrderTypesLabel.BuyingWindow,
          items: this.mapProductToProductData(product),
        },
      });
    }
  }

  // user goes to checkout page
  beginCheckout(order: Order): void {
    if (order && order.items && order.items.length > 0) {
      this.sendGoogleAnalytics({
        event: EventNames.BeginCheckout,
        ecommerce: {
          currency: 'USD',
          value: order.totalCost,
          ...(order.promoCodeExists && { coupon: order.promo_code.code }),
          items: flatternArray(
            order.items.map((oi) =>
              this.mapOrderItemToProductData(/*order, */ oi)
            )
          ),
          // checkout: {
          //   actionField: {
          //     id: order.id,
          //     shipping: order.shippingTotal,
          //     tax: order.taxes,
          //     revenue: order.totalCost,
          //     option: order.creditCard ? 'Credit Card' : 'Terms',
          //     step: 1,
          //     ...(order.promoCodeExists && { coupon: order.promo_code.code }),
          //   },
          // },
        },
      });
    }
  }

  // user submits checkout
  purchase(order: Order): void {
    if (order && order.items && order.items.length > 0) {
      this.sendGoogleAnalytics({
        event: EventNames.Purchase,
        ecommerce: {
          currency: 'USD',
          value: order.totalCost,
          transaction_id: order.id,
          order_type: ORDER_TYPES.find((i) => i.type === order.type).label,
          shipping: order.shippingTotal,
          tax: order.taxes,
          ...(order.promoCodeExists && { coupon: order.promo_code.code }),
          items: flatternArray(
            order.items.map((oi) =>
              this.mapOrderItemToProductData(/*order,*/ oi)
            )
          ),
          // .type
          // purchase: {
          //   actionField: {
          //     id: order.id,
          //     shipping: order.shippingTotal,
          //     tax: order.taxes,
          //     revenue: order.totalCost,
          //     ...(order.promoCodeExists && { coupon: order.promo_code.code }),
          //   },
          //   products: flatternArray(
          //     order.items.map((oi) => this.mapOrderItemToProductData(order, oi))
          //   ),
          // },
        },
      });
    }
  }

  // when get products by multiple Ids
  viewSearchResults(term: string, products: Product[]): void {
    if (term && term.trim().length > 0) {
      this.sendGoogleAnalytics({
        event: 'view_search_results',
        search_term: term,
        products: flatternArray(
          products.map((p) => this.mapProductToProductDataOld(p, true))
        ),
      });
    }
  }

  // view single product (detailed view)
  // view_item items
  viewItem(product: Product): void {
    if (product) {
      this.sendGoogleAnalytics({
        event: 'productClick',
        ecommerce: {
          click: {
            products: this.mapProductToProductDataOld(product),
          },
        },
      });
      this.sendGoogleAnalytics({
        event: 'productDetail',
        ecommerce: {
          detail: {
            products: this.mapProductToProductDataOld(product),
          },
        },
      });
    }
  }

  // view multiple products
  // view_item_list items
  viewItems(products: Product[]): void {
    if (!isEmptyArray(products)) {
      let impressionArray;
      impressionArray = (products as Product[]).map((p) =>
        this.mapProductToProductDataOld(p, true)
      );
      this.sendGoogleAnalytics({
        event: 'productImpressions',
        ecommerce: {
          currencyCode: 'US',
          impressions: flatternArray(impressionArray),
        },
      });
    }
  }

  viewCart(order: Order): void {
    this.sendGoogleAnalytics({
      event: EventNames.ViewCart,
      ecommerce: {
        currency: 'USD',
        value: order.totalCost,
        order_type: ORDER_TYPES.find((i) => i.type === order.type).label,
        items: flatternArray(
          order.items.map((orderItem) =>
            this.mapOrderItemToProductData(orderItem)
          )
        ),
      },
    });
  }

  // old fashion way to send navigation path
  // in modern way should be screen_view	screen_name	a user loads a new screen or new content.
  navigationTo(path: string, title = ''): void {
    if (path) {
      this.sendGoogleAnalytics({
        event: 'virtualPageView',
        page: {
          url: path,
          title,
        },
      });
    }
  }

  private get gtmDefined(): boolean {
    return !!this.dataLayer;
  }

  private sendGoogleAnalytics(data: Object): void {
    if (!this.gtmDefined) {
      window.dataLayer = [];
    }

    this.dataLayer.push(data);
  }

  private mapProductToProductData(
    product: Product,
    hideVariants = false
  ): Array<Item> {
    const program = this.getProductProgram(product);

    if (product.hasVariations) {
      const skus = hideVariants ? [product.skus[0]] : product.skus;

      return skus.map((sku) => {
        return {
          item_id: sku.getDisplaySkuID(),
          item_name: product.label,
          item_brand: product.getBrand(),
          item_category: this.fillCategory(product, sku),
          item_variant: this.getSkuLabel(sku),
          price: sku.getPrice(0),
          ...(program && { item_list_name: program }),
        };
      });
    } else {
      const currentSku = product.firstSku;

      return [
        {
          item_id: product.firstSkuId,
          item_name: product.label,
          item_brand: product.getBrand(),
          item_category: this.fillCategory(product),
          price: product.getPrice(0, currentSku as SKU) || 0.0,
          ...(program && { item_list_name: program }),
        },
      ];
    }
  }

  private mapOrderItemToProductData(
    // order: Order,
    orderItem: OrderItem,
    quantity = 0,
    sku: OrderItemSku = null
  ): Array<Item> {
    // item_id	string	Yes*	Unique ID/SKU for the item.
    // item_name	string	Yes*	Item name.
    // item_brand	string	No	Brand of the item
    // item_category	string	No	Item category
    // item_variant	string	No	Item variant
    // price	number	No	Purchase price of the item
    // quantity	integer	No	Item quantity
    // coupon	string	No	Coupon code for a purchasable item
    // list_position	integer	No	The position of the item in the list

    const product = orderItem.product;
    const program = this.getProductProgram(product);

    if (orderItem.product.hasVariations) {
      const result = [];
      if (sku) {
        let skuQuantity = quantity;
        if (!skuQuantity) {
          if (sku.quantity === sku.init_quantity) {
            skuQuantity = sku.quantity;
          } else {
            skuQuantity = Math.abs(sku.quantity - sku.init_quantity);
          }
        }

        const productSku = product.getSKUbyID(sku.sku_id);
        if (productSku) {
          result.push({
            item_id: productSku.getDisplaySkuID(),
            item_name: product.label,
            item_brand: product.getBrand(),
            item_category: this.fillCategory(orderItem, productSku),
            item_variant: this.getSkuLabel(productSku),
            price: productSku.getPrice(skuQuantity),
            quantity: skuQuantity,
            ...(program && { item_list_name: program }),
          });
        }
      } else {
        const orderSkus = orderItem.skus;
        orderSkus.forEach((orderSku) => {
          let skuQuantity = orderSku.quantity;
          if (orderSku.quantity !== orderSku.init_quantity) {
            skuQuantity = Math.abs(orderSku.quantity - orderSku.init_quantity);
          }

          if (skuQuantity) {
            const productSku = product.getSKUbyID(orderSku.sku_id);
            if (productSku) {
              result.push({
                item_id: productSku.getDisplaySkuID(),
                item_name: product.label,
                item_brand: product.getBrand(),
                item_category: this.fillCategory(orderItem, productSku),
                item_variant: this.getSkuLabel(productSku),
                price: productSku.getPrice(skuQuantity),
                quantity: skuQuantity,
                ...(program && { item_list_name: program }),
              });
            }
          }
        });
      }

      return result;
    } else {
      let orderItemQuantity = quantity;
      if (!orderItemQuantity) {
        if (orderItem.init_quantity === orderItem.quantity) {
          orderItemQuantity = orderItem.quantity;
        } else {
          orderItemQuantity = Math.abs(
            orderItem.quantity - orderItem.init_quantity
          );
        }
      }

      return [
        {
          item_id: orderItem.productSkuId,
          item_name: product.label,
          item_brand: product.getBrand(),
          item_category: this.fillCategory(orderItem),
          price: orderItem.price,
          quantity: orderItemQuantity,
          ...(program && { item_list_name: program }),
        },
      ];
    }
  }

  private mapProductToProductDataOld(product: Product, hideVariants = false) {
    const program = this.getProductProgram(product);

    if (product.hasVariations) {
      const skus = hideVariants ? [product.skus[0]] : product.skus;
      return skus.map((sku) => {
        const item = {
          id: sku.getDisplaySkuID(),
          name: product.label,
          brand: product.getBrand(),
          category: this.fillCategory(product, sku),
          price: '' + sku.getPrice(0),
          variant: this.getSkuLabel(sku),
        };

        if (program) {
          item['list'] = program;
        }

        return item;
      });
    } else {
      let price = 0.0;
      const currentSku = product.firstSku;
      price = product.getPrice(0, currentSku as SKU);

      const item = {
        id: product.firstSkuId,
        name: product.label,
        brand: product.getBrand(),
        category: this.fillCategory(product),
        price: '' + price,
      };

      if (program) {
        item['list'] = program;
      }

      return [item];
    }
  }

  private getSkuLabel(sku: SKU): string {
    const attributes: string[] = [];
    sku.attributeValues.forEach((val, key) => {
      attributes.push(`${this.translateService.translate(key)}: ${val}`);
    });
    return attributes.join(', ');
  }

  private fillCategory(item: OrderItem | Product, sku?: SKU): string {
    if (!item) {
      return '';
    }
    const result = [];
    let product: Product = null;

    if (item instanceof OrderItem) {
      product = item.product;
      result.push(this.getOrderCategory(item));
    }

    if (item instanceof Product) {
      product = item;
      let productSKU = null;
      if (sku) {
        productSKU = sku as SKU;
      }
      const productCategory = this.getProductCategory(product);
      const productType = this.getProductType(product);
      const productSize = this.getProductSize(product, productSKU);
      const productPackageConfig = this.getProductPackageConfig(
        product,
        productSKU
      );
      if (productCategory) {
        result.push(productCategory);
      }
      if (productType) {
        result.push(productType);
      }

      if (productSize) {
        result.push(productSize);
      }
      if (productPackageConfig) {
        result.push(productPackageConfig);
      }
      result.push(this.getOrderCategory(item));
    }

    return result.join('/');
  }

  private getOrderCategory(item: OrderItem | Product): string {
    if (!item) {
      return '';
    }

    if (item instanceof OrderItem) {
      if (item.isBuyingWindowOrder) {
        return 'Buying Window';
      }

      if (item.isOnDemandOrder) {
        return 'On Demand';
      }

      if (item.isCustomOrder) {
        return 'Custom Request';
      }
    }

    if (item instanceof Product) {
      return item.window_id ? 'Buying Window' : 'On Demand';
    }

    return '';
  }

  private getProductProgram(product: Product): string {
    if (!product) {
      return '';
    }

    return product.getProgram();
  }

  private getProductCategory(product: Product): string {
    if (!product) {
      return '';
    }

    return product.productCategory;
  }

  private getProductType(product: Product): string {
    if (!product) {
      return '';
    }

    return product.productType;
  }

  // private getProductSubType(product: Product): string {
  //   if (!product) {
  //     return '';
  //   }

  //   return product.productSubType;
  // }

  private getProductSize(product: Product, sku?: SKU): string {
    if (!product) {
      return '';
    }

    return product.getProductSize(sku);
  }

  private getProductPackageConfig(product: Product, sku?: SKU): string {
    if (!product) {
      return '';
    }
    return product.getProductPackageConfig(sku);
  }

  private get dataLayer(): any[] {
    return window.dataLayer;
  }
}
